import React from 'react';

import { QueryContainer } from '@components/QueryContainer';
import { SpinnerModal } from '@components/SpinnerModal';
import { VerticalSpace } from '@components/VerticalSpace';
import { calendarStrings } from '@data/calendar';
import { warehouseColors, warehouseColorNames } from '@data/warehouse';
import { useDeleteWarehouseHoliday } from '@hooks/mutation/dashboard/useDeleteWarehouseHoliday';
import { usePostWarehouseHoliday } from '@hooks/mutation/dashboard/usePostWarehouseHoliday';
import { useWarehouseHoliday } from '@hooks/queries/dashboard/useWarehouseHoliday';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { createStrings } from '@utils/local';
import { iota } from '@utils/types';
import clsx from 'clsx';
import Color from 'color';
import dayjs from 'dayjs';

const strings = createStrings({
  ja: {
    addHoliday: '+添加假日',
    removeHoliday: '-移除假日',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  calendarTable: {
    ...common.middleText,
    ...common.flexColumn,
    ...common.textGray800,
  },
  calendarRow: {
    ...common.flexInline,
  },
  calendarColumn: {
    flex: 1,
  },
  calendarHeader: {},
  calendarHeaderCell: {
    paddingBottom: 8,
  },
  calendarDate: {
    minHeight: 100,
    padding: 8,
    borderRight: `1px solid ${theme.palette.grey[300]}`,
    borderBottom: `1px solid ${theme.palette.grey[300]}`,
    position: 'relative',
    '&:last-child': {
      borderRight: 0,
    },
    '$calendarRow:last-child &': {
      borderBottom: 0,
    },
  },
  calendarBody: {
    ...common.flexColumn,
    border: `1px solid ${theme.palette.grey[300]}`,
  },
  today: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&.color-pink': { backgroundColor: warehouseColors.pink },
    '&.color-blue': { backgroundColor: warehouseColors.blue },
    '&.color-green': { backgroundColor: warehouseColors.green },
    '&.color-red': { backgroundColor: warehouseColors.red },
    '&.color-brown': { backgroundColor: warehouseColors.brown },
    '&.color-yellow': { backgroundColor: warehouseColors.yellow },
  },
  dateDigit: {
    ...common.flexCenter,
    ...common.fontWeight500,
    height: 24,
    width: 24,
    borderRadius: '50%',
  },
  addHolidayNote: {
    position: 'absolute',
    width: 'calc(100% - 16px)',
    height: 'calc(100% - 16px)',
    borderRadius: 8,
    display: 'none',
    '&.color-pink': {
      backgroundColor: Color(warehouseColors.pink).alpha(0.2).toString(),
    },
    '&.color-blue': {
      backgroundColor: Color(warehouseColors.blue).alpha(0.2).toString(),
    },
    '&.color-green': {
      backgroundColor: Color(warehouseColors.green).alpha(0.2).toString(),
    },
    '&.color-red': {
      backgroundColor: Color(warehouseColors.red).alpha(0.2).toString(),
    },
    '&.color-brown': {
      backgroundColor: Color(warehouseColors.brown).alpha(0.2).toString(),
    },
    '&.color-yellow': {
      backgroundColor: Color(warehouseColors.yellow).alpha(0.2).toString(),
    },
    '$editable$unselected:hover &': {
      ...common.flexCenter,
    },
  },
  holidayNoteBase: {
    ...common.flexCenter,
    marginRight: -9,
    marginLeft: -9,
    backgroundColor: theme.palette.primary.main,
    color: 'transparent',
    '&.color-pink': { backgroundColor: warehouseColors.pink },
    '&.color-blue': { backgroundColor: warehouseColors.blue },
    '&.color-green': { backgroundColor: warehouseColors.green },
    '&.color-red': { backgroundColor: warehouseColors.red },
    '&.color-brown': { backgroundColor: warehouseColors.brown },
    '&.color-yellow': { backgroundColor: warehouseColors.yellow },
    '&$hidden': {
      backgroundColor: 'transparent',
    },
  },
  holidayNoteThick: {
    height: 50,
    transition: 'border-radius 0.2s, margin 0.2s, color 0.2s',
    '$calendarDate:last-child &, $calendarDate:has(+ $editable$calendarDate:hover) &, &$end': {
      borderTopRightRadius: '10px',
      borderBottomRightRadius: '10px',
      marginRight: 0,
    },
    '$calendarDate:first-child &, $calendarDate:hover + $editable$calendarDate &, &$start': {
      borderTopLeftRadius: '10px',
      borderBottomLeftRadius: '10px',
      marginLeft: 0,
    },
    '$editable$calendarDate:hover &': {
      borderRadius: '10px',
      color: theme.palette.grey[800],
      margin: 0,
    },
  },
  holidayNoteThin: {
    marginBottom: 3,
    minHeight: 10,
    '$calendarDate:last-child &, &$end': {
      borderTopRightRadius: '10px',
      borderBottomRightRadius: '10px',
      marginRight: 0,
    },
    '$calendarDate:first-child &, &$start': {
      borderTopLeftRadius: '10px',
      borderBottomLeftRadius: '10px',
      marginLeft: 0,
    },
  },
  start: {},
  end: {},
  hidden: {},
  unselected: {},
  editable: {},
}));

export interface WarehouseCalendarTableProps {
  date: Date;
  agencyId?: number;
  warehouseId?: number;
  warehouseIdList: number[];
  editable?: boolean;
}

export const WarehouseCalendarTable: React.FC<WarehouseCalendarTableProps> = (props) => {
  const { date, agencyId, warehouseId, warehouseIdList, editable } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const monthStart = dayjs(date).startOf('month').startOf('day');
  const monthEnd = dayjs(date).endOf('month').startOf('day');

  const startDate = dayjs(monthStart).subtract((dayjs(monthStart).day() + 6) % 7, 'day');
  const endDate = dayjs(monthEnd).add(6 - ((dayjs(monthEnd).day() + 6) % 7), 'day');

  const warehouseHoliday = useWarehouseHoliday(
    {
      date_after: startDate.format('YYYY-MM-DD'),
      date_before: endDate.format('YYYY-MM-DD'),
      agency_id: agencyId,
      warehouse_id: warehouseId,
    },
    { enabled: !!agencyId },
  );
  const postWarehgouseHoliday = usePostWarehouseHoliday();
  const deleteWarehouseHoliday = useDeleteWarehouseHoliday();

  const days = endDate.diff(startDate, 'day') + 1;
  const rows = Math.ceil(days / 7);

  return (
    <QueryContainer queries={[warehouseHoliday]}>
      <SpinnerModal open={postWarehgouseHoliday.isLoading || deleteWarehouseHoliday.isLoading} />
      <div className={classes.calendarTable}>
        <div className={clsx(classes.calendarRow, classes.calendarHeader)}>
          {iota(7).map((i) => (
            <div key={i} className={clsx(classes.calendarColumn, classes.calendarHeaderCell)}>
              {calendarStrings.weekdaysShort((i + 1) % 7)}
            </div>
          ))}
        </div>
        <div className={clsx(classes.calendarBody)}>
          {iota(rows).map((i) => (
            <div key={i} className={clsx(classes.calendarRow)}>
              {iota(7).map((j) => {
                const currentDate = startDate.add(7 * i + j, 'day');
                const d = currentDate.get('date');
                const thisMonth = currentDate.get('month') === date.getMonth();
                const today = currentDate.isSame(new Date(), 'date');
                const isStart = !warehouseHoliday.data?.find(
                  (h) => h.date === currentDate.subtract(1, 'day').format('YYYY-MM-DD'),
                );
                const isEnd = !warehouseHoliday.data?.find(
                  (h) => h.date === currentDate.add(1, 'day').format('YYYY-MM-DD'),
                );

                const holiday = warehouseHoliday.data?.find(
                  (h) => h.date === currentDate.format('YYYY-MM-DD'),
                );

                return (
                  <div
                    key={j}
                    className={clsx(
                      classes.calendarColumn,
                      classes.calendarDate,
                      !holiday && classes.unselected,
                      editable && classes.baseButton,
                      editable && classes.editable,
                    )}
                  >
                    {warehouseId && (
                      <div
                        className={clsx(
                          classes.addHolidayNote,
                          `color-${warehouseColorNames[warehouseIdList.indexOf(warehouseId)]}`,
                        )}
                        onClick={
                          editable
                            ? async () =>
                                await postWarehgouseHoliday.mutateAsync({
                                  date: currentDate.format('YYYY-MM-DD'),
                                  warehouse: warehouseId,
                                })
                            : undefined
                        }
                      >
                        {strings.addHoliday}
                      </div>
                    )}
                    <div
                      className={clsx(
                        classes.dateDigit,
                        !thisMonth && classes.textGray500,
                        today && classes.today,
                        `color-${warehouseColorNames[warehouseIdList.indexOf(warehouseId ?? 0)]}`,
                      )}
                    >
                      {String(d).padStart(2, '0')}
                    </div>
                    <VerticalSpace height={3} />
                    {warehouseId && holiday && (
                      <div
                        className={clsx(
                          classes.holidayNoteBase,
                          classes.holidayNoteThick,
                          isStart && classes.start,
                          isEnd && classes.end,
                          `color-${warehouseColorNames[warehouseIdList.indexOf(warehouseId)]}`,
                        )}
                        onClick={
                          editable
                            ? async () => await deleteWarehouseHoliday.mutateAsync(holiday.id)
                            : undefined
                        }
                      >
                        {strings.removeHoliday}
                      </div>
                    )}
                    {!warehouseId &&
                      warehouseIdList.map((id, index) => {
                        const holidayById = warehouseHoliday.data?.find(
                          (h) => h.date === currentDate.format('YYYY-MM-DD') && h.warehouse === id,
                        );
                        const isStartById = !warehouseHoliday.data?.find(
                          (h) =>
                            h.date === currentDate.subtract(1, 'day').format('YYYY-MM-DD') &&
                            h.warehouse === id,
                        );
                        const isEndById = !warehouseHoliday.data?.find(
                          (h) =>
                            h.date === currentDate.add(1, 'day').format('YYYY-MM-DD') &&
                            h.warehouse === id,
                        );

                        return (
                          <div
                            key={id}
                            className={clsx(
                              classes.holidayNoteBase,
                              classes.holidayNoteThin,
                              isStartById && classes.start,
                              isEndById && classes.end,
                              !holidayById && classes.hidden,
                              `color-${warehouseColorNames[index]}`,
                            )}
                          ></div>
                        );
                      })}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </QueryContainer>
  );
};
